import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { C, BFI2_ITEMS, BFI2_DOMAINS, HUSTON_ITEMS, AI_LIT_ITEMS,
  CFPB_ITEMS, FSES_ITEMS, OVERCONF_ITEMS, LOSS_AV_ITEMS,
  HELP_SEEK_ITEMS, WTP_ITEMS, WEEKS, WEEK_TOPICS } from '../data/constants';
import { NudgeLogo, PrimaryBtn, SecondaryBtn } from './shared/UI';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LIKERT5 = ['Disagree strongly','Disagree a little','Neutral','Agree a little','Agree strongly'];
const FSES4   = ['Not at all true','Slightly true','Mostly true','Completely true'];

const PHASE_LABEL = { marketing:'Not started',auth:'Registered',consent:'Consented',demographics:'Demographics',baseline:'Baseline done',coaching:'In Coaching',posttest:'Post-Test',complete:'Completed' };
const PHASE_COLOR = { complete:'#059669',coaching:C.teal,posttest:C.navy,baseline:C.gold,demographics:C.purple,consent:C.muted,auth:C.muted,marketing:C.muted };

// ─── tiny helpers ─────────────────────────────────────────────────────────────
const Chip = ({ label, color = C.teal }) => (
  <span style={{ display:'inline-block', fontSize:10, fontWeight:700, padding:'2px 9px', borderRadius:20, background:color+'22', color, textTransform:'uppercase', letterSpacing:'0.06em' }}>
    {label}
  </span>
);

const StatBox = ({ label, value, color = C.navy }) => (
  <div style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:10, padding:'16px 18px', textAlign:'center' }}>
    <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:30, color, fontWeight:400 }}>{value ?? '—'}</div>
    <div style={{ fontSize:11, color:C.muted, marginTop:3 }}>{label}</div>
  </div>
);

const SectionHead = ({ title }) => (
  <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:16, color:C.navy, fontWeight:400, margin:'28px 0 14px', paddingBottom:8, borderBottom:`2px solid ${C.border}` }}>
    {title}
  </div>
);

const ScoreBar = ({ label, value, max = 5, color = C.teal }) => {
  const pct = Math.min(100, ((value - 1) / (max - 1)) * 100);
  return (
    <div style={{ marginBottom:10 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
        <span style={{ fontSize:13, color:C.text }}>{label}</span>
        <span style={{ fontSize:12, color:C.muted, fontWeight:600 }}>{typeof value==='number'?value.toFixed(2):value}/{max}</span>
      </div>
      <div style={{ height:7, background:C.border, borderRadius:4 }}>
        <div style={{ height:'100%', width:`${pct}%`, background:color, borderRadius:4 }} />
      </div>
    </div>
  );
};

// ─── LOGIN ────────────────────────────────────────────────────────────────────
function LoginScreen({ onAuth }) {
  const [pw, setPw]   = useState('');
  const [err, setErr] = useState('');
  const submit = async e => {
    e.preventDefault(); setErr('');
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pw });
      sessionStorage.setItem('nudge-admin-token', data.token);
      onAuth(data.token);
    } catch { setErr('Incorrect password.'); }
  };
  return (
    <div style={{ minHeight:'100vh', background:C.bg, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:24, fontFamily:"'DM Sans',sans-serif" }}>
      <NudgeLogo size="lg" />
      <p style={{ color:C.muted, fontSize:13, margin:'8px 0 28px' }}>Researcher Dashboard</p>
      <form onSubmit={submit} style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:12, padding:32, width:'100%', maxWidth:360 }}>
        <label style={{ display:'block', fontSize:13, fontWeight:600, color:C.text, marginBottom:6 }}>Password</label>
        <input type="password" value={pw} onChange={e=>setPw(e.target.value)} placeholder="Enter admin password"
          style={{ width:'100%', padding:'10px 14px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:14, outline:'none', marginBottom:16, fontFamily:"'DM Sans',sans-serif" }} />
        {err && <p style={{ color:'#dc2626', fontSize:13, marginBottom:12 }}>{err}</p>}
        <PrimaryBtn fullWidth style={{ padding:'12px' }} onClick={undefined}>Access Dashboard</PrimaryBtn>
      </form>
    </div>
  );
}

// ─── MAIN EXPORT ──────────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const [token, setToken]       = useState(() => sessionStorage.getItem('nudge-admin-token') || '');
  const [selected, setSelected] = useState(null); // full participant doc

  if (!token) return <LoginScreen onAuth={setToken} />;
  return selected
    ? <ParticipantDetail data={selected} token={token} onBack={() => setSelected(null)} />
    : <TileGrid token={token} onSelect={setSelected} onLogout={() => { sessionStorage.removeItem('nudge-admin-token'); setToken(''); }} />;
}

// ─── TILE GRID ────────────────────────────────────────────────────────────────
function TileGrid({ token, onSelect, onLogout }) {
  const [stats, setStats]       = useState(null);
  const [participants, setParts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filter, setFilter]     = useState('all');

  const q = path => `${API}${path}?password=${encodeURIComponent(token)}`;

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([ axios.get(q('/admin/stats')), axios.get(q('/admin/participants')) ]);
      setStats(s.data); setParts(p.data);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openParticipant = async (studyId) => {
    try {
      const { data } = await axios.get(q(`/admin/participant/${studyId}`));
      onSelect(data);
    } catch {}
  };

  const filtered = participants.filter(p => {
    const matchSearch = !search || p.study_id?.toLowerCase().includes(search.toLowerCase()) || p.email_masked?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || p.phase === filter;
    return matchSearch && matchFilter;
  });

  return (
    <div style={{ minHeight:'100vh', background:C.bg, fontFamily:"'DM Sans',sans-serif" }}>
      {/* Navbar */}
      <div style={{ background:'#fff', borderBottom:`1px solid ${C.border}`, padding:'0 28px', position:'sticky', top:0, zIndex:10 }}>
        <div style={{ maxWidth:1300, margin:'0 auto', height:56, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <NudgeLogo size="sm" />
            <span style={{ fontSize:13, color:C.muted, fontWeight:600, borderLeft:`1px solid ${C.border}`, paddingLeft:12 }}>Researcher Dashboard</span>
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <SecondaryBtn onClick={load} style={{ padding:'6px 14px', fontSize:13 }}>Refresh</SecondaryBtn>
            <SecondaryBtn onClick={onLogout} style={{ padding:'6px 14px', fontSize:13 }}>Sign out</SecondaryBtn>
          </div>
        </div>
      </div>

      <div style={{ maxWidth:1300, margin:'0 auto', padding:'32px 28px' }}>
        {loading ? <p style={{ color:C.muted, textAlign:'center', marginTop:60 }}>Loading...</p> : (
          <>
            {/* Stats */}
            {stats && (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:14, marginBottom:32 }}>
                <StatBox label="Total Participants" value={stats.total_participants} color={C.navy} />
                <StatBox label="Completed"          value={stats.completed}          color="#059669" />
                <StatBox label="In Coaching"         value={stats.in_coaching}        color={C.teal} />
                <StatBox label="In Survey"           value={stats.in_survey}          color={C.gold} />
                <StatBox label="Conversations"       value={stats.total_conversations} color={C.purple} />
              </div>
            )}

            {/* Filters */}
            <div style={{ display:'flex', gap:12, alignItems:'center', marginBottom:24, flexWrap:'wrap' }}>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by Study ID or email…"
                style={{ padding:'8px 14px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, outline:'none', width:240, fontFamily:"'DM Sans',sans-serif" }} />
              {['all','coaching','complete','baseline','posttest'].map(ph => (
                <button key={ph} onClick={() => setFilter(ph)}
                  style={{ padding:'6px 14px', borderRadius:20, fontSize:12, fontWeight:600, cursor:'pointer', border:`1px solid ${filter===ph?C.teal:C.border}`, background:filter===ph?C.tealLight:'#fff', color:filter===ph?C.teal:C.muted, fontFamily:"'DM Sans',sans-serif" }}>
                  {ph==='all' ? 'All' : PHASE_LABEL[ph] || ph}
                </button>
              ))}
              <span style={{ marginLeft:'auto', fontSize:13, color:C.muted }}>{filtered.length} participant{filtered.length!==1?'s':''}</span>
            </div>

            {/* Tiles */}
            {filtered.length === 0 ? (
              <div style={{ textAlign:'center', padding:'60px 0', color:C.muted, fontSize:15 }}>No participants yet.</div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:18 }}>
                {filtered.map(p => <ParticipantTile key={p.study_id} data={p} onClick={() => openParticipant(p.study_id)} />)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ParticipantTile({ data, onClick }) {
  const profile  = data.profile || {};
  const phase    = data.phase || 'unknown';
  const color    = PHASE_COLOR[phase] || C.muted;
  const lit      = profile.literacyTier;
  const week     = data.coaching_week != null ? `W${data.coaching_week+1}` : null;
  const initials = data.study_id?.slice(4,6) || 'NN';

  return (
    <div onClick={onClick}
      style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:14, padding:'22px 22px 18px', cursor:'pointer', position:'relative', overflow:'hidden' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor=C.teal; e.currentTarget.style.boxShadow='0 4px 20px rgba(13,148,136,0.10)'; e.currentTarget.style.transform='translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor=C.border; e.currentTarget.style.boxShadow='none'; e.currentTarget.style.transform='translateY(0)'; }}
    >
      {/* Color top bar */}
      <div style={{ position:'absolute', top:0, left:0, right:0, height:4, background:color }} />

      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, marginBottom:14 }}>
        {/* Avatar */}
        <div style={{ width:44, height:44, borderRadius:'50%', background:color, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'DM Serif Display',serif", fontSize:16, flexShrink:0 }}>
          {initials}
        </div>
        <Chip label={PHASE_LABEL[phase] || phase} color={color} />
      </div>

      <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:18, color:C.navy, marginBottom:3 }}>{data.study_id}</div>
      <div style={{ fontSize:12, color:C.muted, marginBottom:14 }}>{data.email_masked || 'Email not captured'}</div>

      {/* Quick stats row */}
      <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
        {lit && <span style={{ fontSize:11, background:C.bg, border:`1px solid ${C.border}`, borderRadius:6, padding:'3px 8px', color:C.body }}>Literacy: <b>{lit}</b></span>}
        {profile.overconfidence != null && <span style={{ fontSize:11, background:C.bg, border:`1px solid ${C.border}`, borderRadius:6, padding:'3px 8px', color:C.body }}>OC: <b>{Math.round(profile.overconfidence*100)}%</b></span>}
        {week && <span style={{ fontSize:11, background:C.tealLight, border:`1px solid ${C.border}`, borderRadius:6, padding:'3px 8px', color:C.teal }}>Coaching {week}</span>}
      </div>

      <div style={{ fontSize:11, color:C.muted, marginTop:12 }}>
        Joined {data.created_at ? new Date(data.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—'}
      </div>
    </div>
  );
}

// ─── DETAIL VIEW ──────────────────────────────────────────────────────────────
function ParticipantDetail({ data, token, onBack }) {
  const [tab, setTab] = useState('pre');
  const tabs = [{ id:'pre', label:'Pre-Coaching Survey' }, { id:'chats', label:'Coaching Chats' }, { id:'post', label:'Post-Coaching Survey' }];
  const phase = data.phase || 'unknown';
  const color = PHASE_COLOR[phase] || C.muted;

  return (
    <div style={{ minHeight:'100vh', background:C.bg, fontFamily:"'DM Sans',sans-serif" }}>
      {/* Header */}
      <div style={{ background:'#fff', borderBottom:`1px solid ${C.border}`, padding:'0 28px', position:'sticky', top:0, zIndex:10 }}>
        <div style={{ maxWidth:1100, margin:'0 auto', height:56, display:'flex', alignItems:'center', gap:16 }}>
          <button onClick={onBack} style={{ background:'none', border:`1px solid ${C.border}`, borderRadius:8, padding:'6px 14px', fontSize:13, cursor:'pointer', color:C.body, fontFamily:"'DM Sans',sans-serif", display:'flex', alignItems:'center', gap:6 }}>
            ← Back
          </button>
          <div style={{ width:36, height:36, borderRadius:'50%', background:color, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'DM Serif Display',serif", fontSize:14 }}>
            {data.study_id?.slice(4,6) || 'NN'}
          </div>
          <div>
            <span style={{ fontFamily:"'DM Serif Display',serif", fontSize:18, color:C.navy }}>{data.study_id}</span>
            <span style={{ fontSize:12, color:C.muted, marginLeft:12 }}>{data.email_masked || ''}</span>
          </div>
          <Chip label={PHASE_LABEL[phase] || phase} color={color} />
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ background:'#fff', borderBottom:`1px solid ${C.border}`, padding:'0 28px' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', display:'flex', gap:0 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ background:'none', border:'none', cursor:'pointer', padding:'14px 20px', fontSize:14, fontWeight:600, color:tab===t.id?C.teal:C.body, borderBottom:tab===t.id?`2px solid ${C.teal}`:'2px solid transparent', fontFamily:"'DM Sans',sans-serif" }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth:1100, margin:'0 auto', padding:'32px 28px 80px' }}>
        {tab === 'pre'   && <PreSurveyTab   data={data} />}
        {tab === 'chats' && <ChatsTab       data={data} />}
        {tab === 'post'  && <PostSurveyTab  data={data} />}
      </div>
    </div>
  );
}

// ─── PRE-COACHING SURVEY TAB ──────────────────────────────────────────────────
function PreSurveyTab({ data }) {
  const bl  = data.baseline || {};
  const raw = bl.raw || {};
  if (!Object.keys(raw).length) return <Empty msg="No baseline survey data recorded yet." />;

  const bfi2scores = bl.bfi2 || {};
  const litScores  = bl.literacy || {};

  return (
    <div>
      {/* ── BFI-2 ── */}
      <SectionHead title="Personality Assessment (BFI-2)" />
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:12, marginBottom:20 }}>
        {[{k:'E',label:'Extraversion',color:'#6366f1'},{k:'A',label:'Agreeableness',color:C.teal},{k:'C',label:'Conscientiousness',color:C.mint},{k:'N',label:'Neuroticism',color:C.coral},{k:'O',label:'Openness',color:C.gold}].map(d => (
          <div key={d.k} style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:10, padding:'14px 16px' }}>
            <div style={{ fontSize:11, color:C.muted, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6 }}>{d.label}</div>
            <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:28, color:d.color }}>{bfi2scores[d.k]?.toFixed(2) ?? '—'}<span style={{ fontSize:13, color:C.muted }}>/5</span></div>
            <div style={{ height:5, background:C.border, borderRadius:3, marginTop:8 }}>
              <div style={{ height:'100%', width:`${((bfi2scores[d.k]||1)-1)/4*100}%`, background:d.color, borderRadius:3 }} />
            </div>
          </div>
        ))}
      </div>
      <QATable label="Personality items (I am someone who…)" items={BFI2_ITEMS.map((q,i) => ({ q, a: raw.bfi2?.[i] != null ? LIKERT5[(raw.bfi2[i]||1)-1] : null }))} />

      {/* ── Financial Literacy ── */}
      <SectionHead title="Financial Literacy" />
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:12, marginBottom:20 }}>
        <div style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:10, padding:'14px 16px', textAlign:'center' }}>
          <div style={{ fontSize:11, color:C.muted, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6 }}>Composite</div>
          <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:30, color:C.teal }}>{litScores.composite ?? '—'}<span style={{ fontSize:13, color:C.muted }}>%</span></div>
          <Chip label={litScores.tier || '—'} color={litScores.tier==='high'?'#059669':litScores.tier==='low'?C.coral:C.gold} />
        </div>
        {Object.entries(litScores.domainScores||{}).map(([dom,sc]) => (
          <div key={dom} style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:10, padding:'14px 16px', textAlign:'center' }}>
            <div style={{ fontSize:11, color:C.muted, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6 }}>{dom}</div>
            <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:28, color:C.navy }}>{sc}<span style={{ fontSize:13, color:C.muted }}>%</span></div>
          </div>
        ))}
      </div>
      <QATable label="Literacy questions" items={HUSTON_ITEMS.map((item,i) => ({
        q: item.q,
        a: raw.literacy?.[i] != null ? item.opts[raw.literacy[i]] : null,
        correct: item.correct != null ? item.opts[item.correct] : null,
        isCorrect: item.correct != null && raw.literacy?.[i] === item.correct,
      }))} showCorrect />

      {/* ── AI Literacy ── */}
      <SectionHead title="AI Literacy" />
      <div style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:10, padding:'14px 18px', marginBottom:16, display:'inline-block' }}>
        <span style={{ fontSize:11, color:C.muted, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em' }}>Score </span>
        <span style={{ fontFamily:"'DM Serif Display',serif", fontSize:26, color:C.teal }}>{Math.round(bl.aiLiteracy ?? 0)}</span>
        <span style={{ fontSize:13, color:C.muted }}>/100</span>
      </div>
      <QATable label="AI Literacy items" items={AI_LIT_ITEMS.map((q,i) => ({ q, a: raw.aiLit?.[i] != null ? LIKERT5[(raw.aiLit[i]||1)-1] : null }))} />

      {/* ── Financial Well-Being ── */}
      <SectionHead title="Financial Well-Being (CFPB)" />
      <QATable label="Well-being items" items={CFPB_ITEMS.map((item,i) => ({ q: item.q, a: raw.wellbeing?.[i] != null ? item.scale[raw.wellbeing[i]] : null }))} />

      {/* ── Self-Efficacy ── */}
      <SectionHead title="Financial Self-Efficacy" />
      <QATable label="Self-efficacy items" items={FSES_ITEMS.map((q,i) => ({ q, a: raw.fses?.[i] != null ? (['Not at all true','Slightly true','Mostly true','Completely true'])[raw.fses[i]] : null }))} />

      {/* ── Overconfidence ── */}
      <SectionHead title="Overconfidence Calibration" />
      <div style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:10, padding:'14px 18px', marginBottom:16, display:'inline-block' }}>
        <span style={{ fontSize:11, color:C.muted, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em' }}>Miss rate </span>
        <span style={{ fontFamily:"'DM Serif Display',serif", fontSize:26, color:C.coral }}>{Math.round((bl.overconfidence||0)*100)}<span style={{ fontSize:13, color:C.muted }}>%</span></span>
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {OVERCONF_ITEMS.map((item,i) => {
          const ans    = raw.overconf?.[i];
          const inside = ans && ans.low != null && ans.high != null && item.actual >= ans.low && item.actual <= ans.high;
          return (
            <div key={i} style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:8, padding:'12px 16px', display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
              <span style={{ flex:1, fontSize:13, color:C.text, minWidth:200 }}>{item.q}</span>
              <div style={{ display:'flex', gap:20, alignItems:'center', flexShrink:0 }}>
                <span style={{ fontSize:12, color:C.muted }}>Range: <b style={{ color:C.text }}>{ans?.low ?? '—'} – {ans?.high ?? '—'} {item.unit}</b></span>
                <span style={{ fontSize:12, color:C.muted }}>Actual: <b style={{ color:C.text }}>{item.actual} {item.unit}</b></span>
                <Chip label={ans?.low==null?'Not answered':inside?'Hit':'Miss'} color={ans?.low==null?C.muted:inside?'#059669':C.coral} />
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Loss Aversion ── */}
      <SectionHead title="Loss Aversion Gambles" />
      <div style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:10, padding:'14px 18px', marginBottom:16, display:'inline-block' }}>
        <span style={{ fontSize:11, color:C.muted, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em' }}>Lambda ratio </span>
        <span style={{ fontFamily:"'DM Serif Display',serif", fontSize:26, color:C.navy }}>{(bl.lossAversion||1).toFixed(2)}</span>
      </div>
      <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
        {LOSS_AV_ITEMS.map((item,i) => {
          const ans = raw.lossAv?.[i];
          return (
            <div key={i} style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:10, padding:'14px 16px', textAlign:'center', minWidth:130 }}>
              <div style={{ fontSize:11, color:'#059669', fontWeight:600 }}>Win ${item.gain}</div>
              <div style={{ fontSize:11, color:C.coral, fontWeight:600 }}>Lose ${item.loss}</div>
              <div style={{ marginTop:8 }}>
                <Chip label={ans ?? 'No answer'} color={ans==='accept'?C.teal:ans==='reject'?C.coral:C.muted} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── CHATS TAB ───────────────────────────────────────────────────────────────
function ChatsTab({ data }) {
  const convs  = data.conversations || [];
  const [open, setOpen] = useState(null);
  if (!convs.length) return <Empty msg="No coaching conversations recorded yet." />;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      {convs.map((conv, i) => {
        const msgs = conv.messages || [];
        const sid  = conv.session_id || '';
        const wm   = sid.match(/-w(\d+)-d(\d+)/);
        const wLabel = wm ? `Week ${+wm[1]+1} — ${WEEKS[+wm[1]] || ''} · Session ${+wm[2]+1}: ${WEEK_TOPICS[+wm[1]]?.[+wm[2]] || ''}` : sid;
        const isOpen = open === i;

        return (
          <div key={i} style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
            <div onClick={() => setOpen(isOpen ? null : i)}
              style={{ padding:'14px 18px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', background: isOpen ? '#f0fdfa' : '#fff' }}>
              <div>
                <span style={{ fontSize:14, fontWeight:600, color:C.navy }}>{wLabel}</span>
                <span style={{ fontSize:12, color:C.muted, marginLeft:12 }}>{msgs.length} messages</span>
              </div>
              <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                {conv.updated_at && <span style={{ fontSize:11, color:C.muted }}>{new Date(conv.updated_at).toLocaleDateString()}</span>}
                <span style={{ color:C.teal }}>{isOpen ? '▲' : '▼'}</span>
              </div>
            </div>
            {isOpen && (
              <div style={{ padding:16, borderTop:`1px solid ${C.border}`, background:'#fafbfc', display:'flex', flexDirection:'column', gap:10, maxHeight:500, overflowY:'auto' }}>
                {msgs.map((msg, mi) => (
                  <div key={mi} style={{ display:'flex', justifyContent: msg.role==='user' ? 'flex-end' : 'flex-start' }}>
                    <div style={{ maxWidth:'82%' }}>
                      <div style={{ fontSize:10, fontWeight:700, color: msg.role==='user'?C.navy:C.teal, marginBottom:3, letterSpacing:'0.08em', textTransform:'uppercase' }}>
                        {msg.role==='user' ? 'PARTICIPANT' : 'NUDGE'}
                      </div>
                      <div style={{ padding:'10px 14px', borderRadius: msg.role==='user'?'12px 12px 4px 12px':'12px 12px 12px 4px', background: msg.role==='user'?'#e0f2fe':'#fff', border: msg.role==='user'?'1px solid #bae6fd':`1px solid ${C.border}`, fontSize:13, color:C.text, lineHeight:1.65, whiteSpace:'pre-wrap' }}>
                        {msg.content}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── POST-COACHING SURVEY TAB ─────────────────────────────────────────────────
function PostSurveyTab({ data }) {
  const bl  = data.baseline || {};
  const pt  = data.posttest  || {};
  const raw = pt.raw || {};
  if (!Object.keys(raw).length && !pt.wtp && !pt.referral == null) return <Empty msg="No post-test survey data recorded yet." />;

  const litBl = bl.literacy || {};
  const litPt = pt.literacy || {};

  return (
    <div>
      {/* ── Literacy comparison ── */}
      <SectionHead title="Financial Literacy (Post-Test)" />
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:12, marginBottom:20 }}>
        <CompareCard label="Composite" bl={litBl.composite} pt={litPt.composite} suffix="%" color={C.teal} />
        {Object.entries(litBl.domainScores||{}).map(([dom,sc]) => (
          <CompareCard key={dom} label={dom} bl={sc} pt={litPt.domainScores?.[dom]} suffix="%" color={C.navy} />
        ))}
      </div>
      {raw.literacy && <QATable label="Literacy answers (post-test)" items={HUSTON_ITEMS.map((item,i) => ({
        q: item.q,
        a: raw.literacy?.[i] != null ? item.opts[raw.literacy[i]] : null,
        correct: item.correct != null ? item.opts[item.correct] : null,
        isCorrect: item.correct != null && raw.literacy?.[i] === item.correct,
      }))} showCorrect />}

      {/* ── Well-Being ── */}
      {raw.wellbeing?.length > 0 && (<>
        <SectionHead title="Financial Well-Being (Post-Test)" />
        <QATable label="Well-being items" items={CFPB_ITEMS.map((item,i) => ({ q: item.q, a: raw.wellbeing?.[i] != null ? item.scale[raw.wellbeing[i]] : null }))} />
      </>)}

      {/* ── FSES ── */}
      {raw.fses?.length > 0 && (<>
        <SectionHead title="Financial Self-Efficacy (Post-Test)" />
        <QATable label="" items={FSES_ITEMS.map((q,i) => ({ q, a: raw.fses?.[i] != null ? FSES4[raw.fses[i]] : null }))} />
      </>)}

      {/* ── Overconfidence ── */}
      {raw.overconf?.length > 0 && (<>
        <SectionHead title="Overconfidence Calibration (Post-Test)" />
        <CompareCard label="Miss Rate" bl={Math.round((bl.overconfidence||0)*100)} pt={Math.round((pt.overconfidence||0)*100)} suffix="%" color={C.coral} lowerIsBetter />
        <div style={{ display:'flex', flexDirection:'column', gap:8, marginTop:14 }}>
          {OVERCONF_ITEMS.map((item,i) => {
            const ans = raw.overconf?.[i];
            const inside = ans && ans.low!=null && ans.high!=null && item.actual>=ans.low && item.actual<=ans.high;
            return (
              <div key={i} style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:8, padding:'11px 15px', display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
                <span style={{ flex:1, fontSize:13, color:C.text, minWidth:200 }}>{item.q}</span>
                <span style={{ fontSize:12, color:C.muted }}>Range: <b style={{ color:C.text }}>{ans?.low ?? '—'} – {ans?.high ?? '—'}</b></span>
                <span style={{ fontSize:12, color:C.muted }}>Actual: <b>{item.actual}</b></span>
                <Chip label={ans?.low==null?'No answer':inside?'Hit':'Miss'} color={ans?.low==null?C.muted:inside?'#059669':C.coral} />
              </div>
            );
          })}
        </div>
      </>)}

      {/* ── Loss Aversion ── */}
      {raw.lossAv?.length > 0 && (<>
        <SectionHead title="Loss Aversion (Post-Test)" />
        <CompareCard label="Lambda ratio" bl={(bl.lossAversion||1).toFixed(2)} pt={(pt.lossAversion||1).toFixed(2)} color={C.navy} />
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginTop:14 }}>
          {LOSS_AV_ITEMS.map((item,i) => {
            const ans = raw.lossAv?.[i];
            return (
              <div key={i} style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:10, padding:'12px 16px', textAlign:'center', minWidth:120 }}>
                <div style={{ fontSize:11, color:'#059669', fontWeight:600 }}>Win ${item.gain}</div>
                <div style={{ fontSize:11, color:C.coral, fontWeight:600 }}>Lose ${item.loss}</div>
                <div style={{ marginTop:8 }}><Chip label={ans ?? '—'} color={ans==='accept'?C.teal:ans==='reject'?C.coral:C.muted} /></div>
              </div>
            );
          })}
        </div>
      </>)}

      {/* ── Help-Seeking ── */}
      {raw.helpSeek?.length > 0 && (<>
        <SectionHead title="Professional Advice Attitudes" />
        <QATable label="" items={HELP_SEEK_ITEMS.map((q,i) => ({ q, a: raw.helpSeek?.[i] != null ? LIKERT5[raw.helpSeek[i]] : null }))} />
      </>)}

      {/* ── WTP ── */}
      {pt.wtp?.length > 0 && (<>
        <SectionHead title="Willingness to Pay" />
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {WTP_ITEMS.map((item,i) => (
            <div key={i} style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:8, padding:'12px 16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:13, color:C.text }}>{item.q}</span>
              <span style={{ fontWeight:700, color:C.teal, fontSize:14 }}>{pt.wtp[i] ?? '—'}</span>
            </div>
          ))}
        </div>
      </>)}

      {/* ── Advisor Referral ── */}
      {pt.referral != null && (<>
        <SectionHead title="Advisor Connection Request" />
        <div style={{ background: pt.referral ? '#f0fdf4' : '#fff', border:`1px solid ${pt.referral?'#a7f3d0':C.border}`, borderRadius:10, padding:'18px 22px', display:'flex', alignItems:'center', gap:16 }}>
          <div style={{ width:40, height:40, borderRadius:'50%', background:pt.referral?'#059669':C.muted, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>
            {pt.referral ? '✓' : '✗'}
          </div>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:pt.referral?'#059669':C.body }}>{pt.referral ? 'Yes — wants to connect with an advisor' : 'No — declined advisor connection'}</div>
            <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>Post-coaching advisor referral response</div>
          </div>
        </div>
      </>)}
    </div>
  );
}

// ─── Shared sub-components ────────────────────────────────────────────────────
function QATable({ label, items }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items : items.slice(0, 5);
  return (
    <div style={{ marginBottom:20 }}>
      {label && <div style={{ fontSize:12, fontWeight:600, color:C.muted, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:8 }}>{label}</div>}
      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
        {shown.map((row, i) => (
          <div key={i} style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:8, padding:'10px 14px', display:'flex', gap:12, alignItems:'flex-start' }}>
            <span style={{ fontSize:11, color:C.muted, fontWeight:700, minWidth:24, marginTop:1 }}>{i+1}.</span>
            <span style={{ flex:1, fontSize:13, color:C.text, lineHeight:1.55 }}>{row.q}</span>
            <div style={{ flexShrink:0, textAlign:'right' }}>
              {row.a != null ? (
                <>
                  <span style={{ fontSize:12, fontWeight:600, color: row.isCorrect===true ? '#059669' : row.isCorrect===false ? C.coral : C.navy }}>{row.a}</span>
                  {row.isCorrect === false && row.correct && (
                    <div style={{ fontSize:11, color:'#059669', marginTop:2 }}>✓ {row.correct}</div>
                  )}
                </>
              ) : <span style={{ fontSize:12, color:C.muted }}>—</span>}
            </div>
          </div>
        ))}
      </div>
      {items.length > 5 && (
        <button onClick={() => setExpanded(!expanded)}
          style={{ marginTop:8, background:'none', border:`1px solid ${C.border}`, borderRadius:6, padding:'6px 16px', fontSize:12, color:C.body, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>
          {expanded ? `Hide (showing all ${items.length})` : `Show all ${items.length} items`}
        </button>
      )}
    </div>
  );
}

function CompareCard({ label, bl, pt, suffix = '', color = C.teal, lowerIsBetter = false }) {
  const blNum = parseFloat(bl);
  const ptNum = parseFloat(pt);
  const diff  = (!isNaN(blNum) && !isNaN(ptNum)) ? ptNum - blNum : null;
  const improved = diff != null ? (lowerIsBetter ? diff < 0 : diff > 0) : null;
  return (
    <div style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:10, padding:'14px 16px' }}>
      <div style={{ fontSize:11, color:C.muted, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:10 }}>{label}</div>
      <div style={{ display:'flex', gap:16, alignItems:'flex-end' }}>
        <div>
          <div style={{ fontSize:10, color:C.muted, marginBottom:2 }}>Pre</div>
          <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:22, color:C.muted }}>{bl ?? '—'}{suffix}</div>
        </div>
        <div style={{ fontSize:18, color:C.muted }}>→</div>
        <div>
          <div style={{ fontSize:10, color:C.muted, marginBottom:2 }}>Post</div>
          <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:22, color }}>{pt ?? '—'}{suffix}</div>
        </div>
        {diff != null && (
          <div style={{ marginLeft:'auto' }}>
            <Chip label={`${diff>0?'+':''}${typeof diff==='number'?diff.toFixed(1):diff}${suffix}`} color={improved?'#059669':C.coral} />
          </div>
        )}
      </div>
    </div>
  );
}

function Empty({ msg }) {
  return <div style={{ textAlign:'center', padding:'60px 0', color:C.muted, fontSize:15 }}>{msg}</div>;
}
